"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";

const contactOptions = [
  {
    title: "Custom quotes and enterprise solutions",
    email: "hello@key-stone.app",
  },
  {
    title: "Marketing and brand partnerships",
    email: "marketing@key-stone.app",
  },
  {
    title: "24/7 technical assistance and troubleshooting",
    email: "support@key-stone.app",
  },
];

export const LetsConnectSection = () => {
  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setStatus("error");
        setErrorMsg(data.error || "Something went wrong.");
        return;
      }

      setStatus("sent");
      setFormData({ lastName: "", firstName: "", email: "", phone: "", message: "" });
      setTimeout(() => setStatus("idle"), 3000);
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Reset status when user starts typing again
    if (status === "sent" || status === "error") setStatus("idle");
  };

  return (
    <section id="contact" className="flex flex-col gap-24 items-center justify-center pb-32 px-4 md:px-16 lg:px-32 relative z-10 overflow-hidden bg-[#212121]">
      {/* Section Header */}
      <div className="flex flex-col gap-6 items-center w-full">
        <p className="text-[32px] md:text-[48px] font-medium md:font-normal text-[#f8f7f4] text-center leading-[40px] md:leading-[56px] tracking-[-0.96px] md:tracking-[-1.44px] max-w-[537px]">
          Ready to transform your <span className="whitespace-nowrap">e-commerce</span> business?
        </p>
        <p className="text-[16px] md:text-[24px] font-light text-[#c4c4c4] text-center leading-5 md:leading-8 tracking-[-0.032px] md:tracking-[-0.048px] max-w-[248px] md:max-w-[560px]">
          Choose your preferred contact method and let&apos;s build something
          extraordinary together.
        </p>
      </div>

      {/* Contact Card */}
      <div className="flex flex-col md:flex-row items-stretch w-full max-w-[982px] bg-[#1e1e1e]">
        {/* Left - Form */}
        <div
          className="flex flex-col justify-between gap-10 px-6 md:px-8 py-8 border-2 border-[#3e3e3e] rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none md:flex-1"
        >
          <h2 className="font-normal text-[30px] text-white tracking-[-0.6px]">
            Let&apos;s connect
          </h2>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="flex flex-col md:flex-row gap-5">
              <FormField
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
              />
              <FormField
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>

            <FormField
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
            />

            <FormField
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
            />

            <FormField
              type="textarea"
              name="message"
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
            />

            {status === "error" && (
              <p className="text-[14px] text-red-400 leading-5">{errorMsg}</p>
            )}
            {status === "sent" && (
              <p className="text-[14px] text-emerald-400 leading-5">Message sent successfully!</p>
            )}

            <Button
              variant="secondary"
              type="submit"
              className="w-full"
              disabled={status === "sending"}
            >
              {status === "sending" ? "Sending..." : "Send message"}
            </Button>
          </form>
        </div>

        {/* Right - Contact Options */}
        <div className="flex flex-col border-2 border-[#3e3e3e] rounded-b-2xl md:rounded-r-2xl md:rounded-bl-none md:flex-1">
          {contactOptions.map((option, index) => (
            <a
              key={index}
              href={`mailto:${option.email}`}
              className={`group flex-1 px-6 md:px-8 py-8 flex flex-col justify-center gap-6 hover:bg-white/[0.02] transition-colors ${
                index < contactOptions.length - 1
                  ? "border-b-2 border-[#3e3e3e]"
                  : ""
              }`}
            >
              <p className="text-[20px] font-medium text-[#f8f7f4] leading-6 tracking-[-0.4px]">
                {option.title}
              </p>
              <span className="inline-flex items-center gap-1 text-[16px] text-[#f8f7f4] leading-4 py-2 transition-transform duration-300 group-hover:translate-x-2">
                <svg
                  width="16"
                  height="12"
                  viewBox="0 0 15 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.25 4.9165L0.75 4.9165"
                    stroke="#f8f7f4"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.91663 0.75L13.3762 4.20956C13.7095 4.54289 13.8762 4.70956 13.8762 4.91667C13.8762 5.12377 13.7095 5.29044 13.3762 5.62377L9.91663 9.08333"
                    stroke="#f8f7f4"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {option.email}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
